/**
 * Map Chip Service
 *
 * Single source of truth for the four Native Interactive Site Map chips: their keys and display
 * labels. A chip's key doubles as the raw lot status string (see enums/lotStatus.js) and as the
 * value stored in searchParameters.lotStatus (per redux/STORE_DESIGN.md — there is no standalone
 * activeChip field; the chip is derived directly from that single field).
 *
 * @module nativeInteractiveSiteMap/services/mapChipService
 */

import LotStatus from '../enums/lotStatus';

const AVAILABLE_KEY = LotStatus.Available.toString();
const MODEL_KEY = LotStatus.Model.toString();
const COMING_SOON_KEY = LotStatus.ComingSoon.toString();
const SOLD_KEY = LotStatus.Sold.toString();

/**
 * `priority` is the Map Chips Display Logic default-selection order (NHS20-8054): lower wins.
 * It is independent of this array's own order (which only controls render/visual order in
 * components/mapChips.dom.js) — reordering the array to change how chips lay out on screen
 * must not silently change which chip wins the default-selection fallback.
 */
export const MAP_CHIPS = [
  { key: AVAILABLE_KEY,   label: 'Available',   priority: 1 },
  { key: MODEL_KEY,       label: 'Model homes', priority: 2 },
  { key: COMING_SOON_KEY, label: 'Coming soon', priority: 3 },
  { key: SOLD_KEY,        label: 'Sold',        priority: 4 },
];

const DEFAULT_CHIP_KEY = AVAILABLE_KEY;

/**
 * @param {{ lotStatus: string|null }} searchParameters
 * @returns {string} chip key
 */
export function deriveActiveChip(searchParameters) {
  return searchParameters.lotStatus ?? DEFAULT_CHIP_KEY;
}

/**
 * Picks the chip that should become active when the currently active one has nothing to show:
 * the visible chip (count > 0) with the lowest `priority` — the Map Chips Display Logic
 * hierarchy/default-selection order (NHS20-8054). Weighing by `priority` rather than array
 * position keeps this correct even if MAP_CHIPS is ever reordered for visual/layout reasons.
 *
 * @param {Record<string, number>} counts - chipKey -> count, e.g. from selectChipLotCounts
 * @returns {{ key: string, label: string, priority: number }|null} the highest-priority chip
 * with something to show, or null if none do
 */
export function deriveChipByHierarchy(counts) {
  return MAP_CHIPS.reduce((best, chip) => {
    if ((counts[chip.key] ?? 0) <= 0) {
      return best;
    }
    return (!best || chip.priority < best.priority) ? chip : best;
  }, null);
}
